import React, { useState } from 'react';
import SEO from '../../Components/Common/SEO';
import {
    Home,
    Users,
    Target,
    Award,
    TrendingUp,
    Shield,
    Heart,
    Globe,
    ArrowLeft,
    Star,
    MapPin,
    Calendar,
    Building,
    Zap,
    CheckCircle,
    Phone,
    Mail,
    Linkedin,
    Twitter,
    Facebook,
    Instagram,
    ChevronRight,
    Briefcase,
    Clock,
    Code,
    Lightbulb,
    Handshake
} from 'lucide-react';

const About = () => {
    const [activeValue, setActiveValue] = useState(0);

    const stats = [
        { label: 'Active Listings', value: '10,000+', icon: Building },
        { label: 'Happy Tenants', value: '25,000+', icon: Users },
        { label: 'Property Managers', value: '500+', icon: Briefcase },
        { label: 'Cities Covered', value: '15+', icon: MapPin }
    ];

    const values = [
        {
            title: 'Trust & Transparency',
            icon: Shield,
            description: 'We believe in honest communication and transparent processes. Every property listing is verified, and all parties have access to clear, comprehensive information.',
            color: 'highland'
        },
        {
            title: 'Innovation',
            icon: Lightbulb,
            description: 'We leverage cutting-edge technology to simplify the rental process, making it faster, more efficient, and more secure for everyone involved.',
            color: 'jacaranda'
        },
        {
            title: 'Customer Success',
            icon: Heart,
            description: 'Your success is our success. We\'re committed to helping tenants find their perfect home and property managers maximize their returns.',
            color: 'murram'
        },
        {
            title: 'Excellence',
            icon: Star,
            description: 'We strive for excellence in everything we do, from our platform design to customer service, ensuring the highest quality experience.',
            color: 'dawn'
        }
    ];

    const team = [
        {
            name: 'Mark Kieru',
            role: 'Founder & CEO',
            bio: 'A seasoned real estate professional with a passion for delivering exceptional property management services.'
        },
        {
            name: 'Juliana Wanjiku',
            role: 'Operations Manager',
            bio: 'Ensures smooth day-to-day operations and maintains high service standards across all managed properties.'
        },
        {
            name: 'Kharbie',
            role: 'Property Coordinator',
            bio: 'Expert in tenant relations and property maintenance coordination with keen attention to detail.'
        },
        {
            name: 'Cindy Wanjiru',
            role: 'Finance & Accounts',
            bio: 'Handles all financial reporting and ensures accurate, timely rent collection and disbursement.'
        }
    ];

    const milestones = [
        {
            year: '2022',
            title: 'Company Founded',
            description: 'Rent Utopia was established with a vision to revolutionize property rental in Kenya through technology.'
        },
        {
            year: '2023',
            title: 'Platform Launch',
            description: 'Launched our beta platform in Nairobi, connecting the first 100 properties with qualified tenants.'
        },
        {
            year: '2024',
            title: 'Series A Funding',
            description: 'Raised $2M Series A to expand across Kenya and enhance our AI-powered matching algorithms.'
        },
        {
            year: '2025',
            title: 'National Expansion',
            description: 'Extended services to 15+ cities across Kenya, becoming the leading rental platform in the country.'
        }
    ];

    const achievements = [
        {
            title: 'Best PropTech Startup 2024',
            organization: 'Kenya Tech Awards',
            description: 'Recognized for innovation in property technology'
        },
        {
            title: 'Top 10 Kenyan Startups',
            organization: 'TechCrunch Africa',
            description: 'Featured among most promising African startups'
        },
        {
            title: 'Customer Choice Award',
            organization: 'Kenya Property Awards',
            description: 'Highest customer satisfaction in rental services'
        },
        {
            title: 'Digital Innovation Award',
            organization: 'Central Bank of Kenya',
            description: 'Excellence in financial technology integration'
        }
    ];

    const VALUE_COLOR_CLASSES = {
        highland: { border: 'border-highland-500', bg: 'bg-highland-50', iconBg: 'bg-highland-100', text: 'text-highland-500' },
        jacaranda: { border: 'border-jacaranda-500', bg: 'bg-jacaranda-100', iconBg: 'bg-jacaranda-100', text: 'text-jacaranda-500' },
        murram: { border: 'border-murram-500', bg: 'bg-murram-100', iconBg: 'bg-murram-100', text: 'text-murram-500' },
        dawn: { border: 'border-dawn-500', bg: 'bg-dawn-100', iconBg: 'bg-dawn-100', text: 'text-dawn-600' },
    };

    const ValueCard = ({ value, index }) => {
        const Icon = value.icon;
        const isActive = activeValue === index;
        const colors = VALUE_COLOR_CLASSES[value.color];

        return (
            <div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                    isActive
                        ? `${colors.border} ${colors.bg} shadow-lg`
                        : 'border-soil-100 bg-white hover:border-soil-400 hover:shadow-md'
                }`}
                onClick={() => setActiveValue(index)}
            >
                <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${colors.iconBg}`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-display text-lg font-semibold text-soil-900 mb-2">{value.title}</h3>
                        <p className="text-soil-600 text-sm leading-relaxed">{value.description}</p>
                    </div>
                </div>
            </div>
        );
    };

    const TeamCard = ({ member }) => (
        <div className="bg-white rounded-xl shadow-sm border border-soil-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
                <div className="text-center mb-4">
                    <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-highland-500 flex items-center justify-center text-white text-xl font-bold font-display">
                        {member.name[0]}
                    </div>
                    <h3 className="font-display text-xl font-semibold text-soil-900">{member.name}</h3>
                    <p className="text-highland-500 font-medium">{member.role}</p>
                </div>
                <p className="text-soil-600 text-sm leading-relaxed">{member.bio}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-karibu-100">
            <SEO
                title="About Us"
                description="Learn about Rent Utopia's mission to simplify property rental in Kenya, meet our team, and discover what drives us."
                canonicalPath="/about"
            />
            {/* Hero Section */}
            <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-6">
                            <div className="p-4 bg-white/10 rounded-full">
                                <Home className="w-12 h-12" />
                            </div>
                        </div>
                        <h1 className="font-display text-5xl font-bold mb-6">About Rent Utopia</h1>
                        <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto leading-relaxed">
                            We're revolutionizing the rental experience in Kenya by connecting property seekers
                            with quality homes through innovative technology and trusted relationships.
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1" />
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">About</span>
                </div>
            </div>

            {/* Stats Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div key={index} className="bg-white rounded-xl shadow-sm border border-soil-100 p-6 text-center">
                                <Icon className="w-8 h-8 text-highland-500 mx-auto mb-3" />
                                <h3 className="text-2xl font-bold text-soil-900 mb-1">{stat.value}</h3>
                                <p className="text-soil-600 text-sm">{stat.label}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mission & Vision */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid lg:grid-cols-2 gap-12">
                    <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                            <Target className="w-8 h-8 text-highland-500" />
                            <h2 className="font-display text-2xl font-bold text-soil-900">Our Mission</h2>
                        </div>
                        <p className="text-soil-600 leading-relaxed mb-6">
                            To transform the rental experience in Kenya by providing a secure, transparent, and efficient
                            platform that connects property seekers with their ideal homes while empowering property
                            managers to maximize their returns.
                        </p>
                        <ul className="space-y-3">
                            {[
                                'Simplify the rental process for all parties',
                                'Ensure transparency in all transactions',
                                'Provide verified, quality property listings',
                                'Foster trust between tenants and landlords'
                            ].map((item, index) => (
                                <li key={index} className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-highland-500" />
                                    <span className="text-soil-600">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                        <div className="flex items-center space-x-3 mb-6">
                            <TrendingUp className="w-8 h-8 text-jacaranda-500" />
                            <h2 className="font-display text-2xl font-bold text-soil-900">Our Vision</h2>
                        </div>
                        <p className="text-soil-600 leading-relaxed mb-6">
                            To become Africa's leading property rental platform, setting the standard for innovation,
                            trust, and customer satisfaction in the real estate technology sector.
                        </p>
                        <div className="space-y-4">
                            {[
                                { label: 'Innovation Leader', desc: 'Pioneering new technologies in PropTech' },
                                { label: 'Market Expansion', desc: 'Expanding across East Africa by 2027' },
                                { label: 'Digital Transformation', desc: 'Digitalizing Africa\'s rental market' },
                                { label: 'Community Impact', desc: 'Improving housing access for millions' }
                            ].map((goal, index) => (
                                <div key={index} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-jacaranda-500 rounded-full mt-2"></div>
                                    <div>
                                        <h4 className="font-medium text-soil-900">{goal.label}</h4>
                                        <p className="text-soil-600 text-sm">{goal.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Our Story */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                    <h2 className="font-display text-3xl font-bold text-soil-900 mb-8 text-center">Our Story</h2>

                    <div className="max-w-4xl mx-auto">
                        <p className="text-soil-600 leading-relaxed mb-6">
                            Rent Utopia was born from the frustration of searching for quality rental properties in Kenya.
                            Our founders experienced firsthand the challenges of navigating outdated processes, unclear
                            listings, and lack of transparency in the rental market.
                        </p>

                        <p className="text-soil-600 leading-relaxed mb-6">
                            In 2022, we set out to solve these problems by creating a modern, technology-driven platform
                            that would benefit both property seekers and property managers. Drawing inspiration from
                            successful property management systems and international best practices, we built Rent Utopia
                            to address the unique needs of the Kenyan market.
                        </p>

                        <p className="text-soil-600 leading-relaxed">
                            Today, we're proud to have facilitated thousands of successful rentals, helped property
                            managers optimize their operations, and provided tenants with a seamless, trustworthy
                            rental experience. Our journey has just begun, and we're excited about the future of
                            PropTech in Kenya and beyond.
                        </p>
                    </div>
                </div>
            </div>

            {/* Our Values */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">Our Values</h2>
                    <p className="text-soil-600 max-w-2xl mx-auto">
                        These core values guide everything we do and shape how we interact with our customers,
                        partners, and each other.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    {values.map((value, index) => (
                        <ValueCard key={index} value={value} index={index} />
                    ))}
                </div>
            </div>

            {/* Timeline */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                    <h2 className="font-display text-3xl font-bold text-soil-900 mb-8 text-center">Our Journey</h2>

                    <div className="space-y-8">
                        {milestones.map((milestone, index) => (
                            <div key={index} className="flex items-start space-x-6">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-highland-50 rounded-full flex items-center justify-center">
                                        <span className="font-mono text-highland-500 font-bold text-sm">{milestone.year}</span>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-semibold text-soil-900 mb-2">{milestone.title}</h3>
                                    <p className="text-soil-600">{milestone.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Team Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">Meet Our Team</h2>
                    <p className="text-soil-600 max-w-2xl mx-auto">
                        Our diverse team combines deep expertise in technology, real estate, and customer experience
                        to deliver exceptional results.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {team.map((member, index) => (
                        <TeamCard key={index} member={member} />
                    ))}
                </div>
            </div>

{/*             Achievements
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
                    <div className="text-center mb-8">
                        <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">Recognition & Awards</h2>
                        <p className="text-soil-600">
                            We're honored to be recognized by industry leaders and organizations for our innovation and impact.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {achievements.map((achievement, index) => (
                            <div key={index} className="p-6 bg-gray-50 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <Award className="w-6 h-6 text-dawn-500 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-soil-900 mb-1">{achievement.title}</h3>
                                        <p className="text-highland-500 text-sm font-medium mb-2">{achievement.organization}</p>
                                        <p className="text-soil-600 text-sm">{achievement.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>*/}

            {/* Technology & Innovation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="font-display text-3xl font-bold text-soil-900 mb-6">Technology & Innovation</h2>
                        <p className="text-soil-600 mb-6 leading-relaxed">
                            At Rent Utopia, we leverage cutting-edge technology to create seamless experiences.
                            Our platform is built on modern cloud infrastructure with AI-powered matching,
                            advanced security protocols, and mobile-first design.
                        </p>

                        <div className="space-y-4">
                            {[
                                { icon: Zap, title: 'AI-Powered Matching', desc: 'Smart algorithms connect tenants with ideal properties' },
                                { icon: Shield, title: 'Bank-Level Security', desc: 'End-to-end encryption and secure payment processing' },
                                { icon: Code, title: 'Mobile-First Design', desc: 'Optimized experience across all devices' },
                                { icon: Globe, title: 'Cloud Infrastructure', desc: '99.9% uptime with scalable architecture' }
                            ].map((tech, index) => (
                                <div key={index} className="flex items-start space-x-3">
                                    <tech.icon className="w-6 h-6 text-highland-500 mt-1" />
                                    <div>
                                        <h4 className="font-semibold text-soil-900">{tech.title}</h4>
                                        <p className="text-soil-600 text-sm">{tech.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-highland-50 to-karibu-200 p-8 rounded-xl">
                        <div className="grid grid-cols-2 gap-6">
                            {[
                                { label: 'API Endpoints', value: '200+' },
                                { label: 'Uptime', value: '99.9%' },
                                { label: 'Response Time', value: '<200ms' },
                                { label: 'Data Centers', value: '3' }
                            ].map((metric, index) => (
                                <div key={index} className="text-center">
                                    <div className="text-2xl font-bold text-highland-500 mb-1">{metric.value}</div>
                                    <div className="text-soil-600 text-sm">{metric.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact & Connect */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="relative bg-highland-700 rounded-xl text-karibu-100 p-8 overflow-hidden">
                    <div
                        aria-hidden="true"
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[280px] rounded-arch bg-gradient-to-b from-dawn-500/20 to-transparent blur-2xl"
                    />
                    <div className="relative text-center mb-8">
                        <h2 className="font-display text-3xl font-bold mb-4">Get in Touch</h2>
                        <p className="text-karibu-200/80 max-w-2xl mx-auto">
                            Have questions about Rent Utopia or want to learn more about our mission?
                            We'd love to hear from you.
                        </p>
                    </div>

                    <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="text-center">
                            <Phone className="w-8 h-8 mx-auto mb-3 text-dawn-400" />
                            <h3 className="font-semibold mb-2">Call Us</h3>
                            <p className="text-karibu-200/80">+254 712 292 899</p>
                        </div>

                        <div className="text-center">
                            <Mail className="w-8 h-8 mx-auto mb-3 text-dawn-400" />
                            <h3 className="font-semibold mb-2">Email Us</h3>
                            <p className="text-karibu-200/80">itorama254@gmail.com</p>
                        </div>

                        <div className="text-center">
                            <MapPin className="w-8 h-8 mx-auto mb-3 text-dawn-400" />
                            <h3 className="font-semibold mb-2">Visit Us</h3>
                            <p className="text-karibu-200/80">Westlands, Nairobi</p>
                        </div>

                        <div className="text-center">
                            <Users className="w-8 h-8 mx-auto mb-3 text-dawn-400" />
                            <h3 className="font-semibold mb-2">Follow Us</h3>
                            <div className="flex justify-center space-x-3">
                                <Twitter className="w-5 h-5 text-karibu-200/80 cursor-pointer hover:text-white" />
                                <Linkedin className="w-5 h-5 text-karibu-200/80 cursor-pointer hover:text-white" />
                                <Facebook className="w-5 h-5 text-karibu-200/80 cursor-pointer hover:text-white" />
                                <Instagram className="w-5 h-5 text-karibu-200/80 cursor-pointer hover:text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;